import { deepmerge } from '@mui/utils';
import { alpha, createTheme, type CSSObject, type Theme, type ThemeOptions } from '@mui/material/styles';
import type { RaThemeOptions } from 'react-admin';
import { MOCKUP_CLASS, MOCKUP_LOOK_ATTR, MOCKUP_SEVERITY_ATTR, MOCKUP_TOKENS as T, type MockupSeverity } from './tokens';

/**
 * Motyw „Wygląd z makiety” (status 28.10.2026 pkt 3, decyzja użytkownika
 * 28.10.2026, przełącznik `mockupLook`, S-28.10-01).
 *
 * ## Jak
 *
 * Nadpisania motywu MUI (`components.*.styleOverrides`, typografia, paleta)
 * nakładane na motyw aplikacji (`theme.ts`) — ekrany zostają bez zmian,
 * zmienia się wygląd kontrolek, z których są zbudowane: karty, tabele,
 * znaczniki, przyciski, komunikaty, zakładki, dymki, pola. Elementy, których
 * motyw MUI nie rozpozna (tytuł karty sekcji, kafelek pulpitu, menu i treść
 * react-admina), dostają style przez klasy-zaczepy z `tokens.ts`
 * (`mockupGlobalStyles`).
 *
 * ## Tekst zawsze się mieści (decyzja użytkownika 28.10.2026)
 *
 * Makieta miejscami ucina tekst (stawka „1550 EU…” w banku). Tu reguła
 * odwrotna: znacznik i nagłówek kolumny zawijają się zamiast ucinać, długie
 * słowo łamie się dopiero, gdy nie mieści się w całości, a dymek nie ma
 * sztywnej szerokości. Żadna reguła nie ustala szerokości ani nie wyłącza
 * zawijania — tego pilnuje test `mockupTheme.test.ts`.
 */

/** Nadpisania motywu — część wspólna dla każdej bazy (`mockupThemeOptions`). */
export const mockupThemeOptions: ThemeOptions = {
  palette: {
    primary: { main: T.blue, dark: T.blueDark, contrastText: '#fff' },
    secondary: { main: T.appbar, contrastText: '#fff' },
    info: { main: T.info },
    success: { main: T.green },
    error: { main: T.red },
    warning: { main: T.orange },
    text: { primary: T.text, secondary: T.text2 },
    divider: T.line,
    background: { default: T.pageBg, paper: T.panel },
  },
  shape: { borderRadius: T.radius },
  typography: {
    fontFamily: T.fontFamily,
    fontSize: T.fontSize,
    // Tytuł sekcji i okna — 17 px / 500 jak `.card h2` makiety.
    h6: { fontSize: T.sectionTitleSize, fontWeight: 500, lineHeight: '24px', letterSpacing: '.15px' },
    h5: { fontSize: 20, fontWeight: 700, lineHeight: 1.3 },
    // Nagłówek ekranu (instrukcja obsługi) — 24 px / 700 jak `h1` makiety (partia 2).
    h4: { fontSize: T.pageTitleSize, fontWeight: 700, lineHeight: 1.25 },
    subtitle1: { fontWeight: 500 },
    overline: { fontSize: T.capsSize, letterSpacing: T.capsSpacing, lineHeight: 1.6, fontWeight: 400 },
    button: { fontWeight: 500, letterSpacing: '.4px' },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px rgba(0,0,0,.14)',
        },
        colorSecondary: { backgroundColor: T.appbar, color: '#fff' },
      },
    },
    MuiCard: {
      // Karta jak `.card` makiety: cienka ramka, bez cienia.
      styleOverrides: {
        root: { boxShadow: 'none', border: `1px solid ${T.line}`, borderRadius: T.radius },
      },
    },
    MuiPaper: {
      styleOverrides: {
        outlined: { borderColor: T.line },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        title: { fontSize: T.sectionTitleSize, fontWeight: 500, lineHeight: '24px', letterSpacing: '.15px' },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        // Nagłówek kolumny szary, 13 px, zawija się zamiast ucinać.
        head: {
          color: T.text2,
          fontSize: 13,
          fontWeight: 500,
          lineHeight: 1.35,
          whiteSpace: 'normal',
          backgroundColor: T.panel,
        },
        root: { borderBottomColor: T.line, overflowWrap: 'break-word' },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.MuiTableRow-hover:hover': { backgroundColor: T.blueHover },
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: T.blueHover },
        },
      },
    },
    MuiChip: {
      // „Pigułka” makiety: 24 px, 13 px, promień 16 — ale długa treść
      // zawija się w kolejny wiersz, zamiast ginąć za wielokropkiem.
      styleOverrides: {
        root: {
          fontSize: 13,
          borderRadius: 16,
          height: 'auto',
          minHeight: 24,
          maxWidth: '100%',
        },
        sizeSmall: { minHeight: 20, fontSize: 12 },
        label: {
          whiteSpace: 'normal',
          overflowWrap: 'break-word',
          lineHeight: 1.35,
          paddingTop: 2,
          paddingBottom: 2,
        },
        outlined: ({ ownerState }) =>
          ownerState.color === 'default' || ownerState.color === undefined
            ? { borderColor: T.chipBorder }
            : {},
        // Stan w kolorze jak „pigułka” makiety (`.chip.a`): jasne tło, ciemny
        // tekst w kolorze stanu — zamiast pełnego koloru z białym napisem.
        filled: ({ ownerState, theme }) => {
          const color = ownerState.color;
          if (!color || color === 'default') return {};
          const entry = (theme as Theme).palette[color];
          if (!entry || typeof entry !== 'object' || !('main' in entry)) return {};
          return {
            backgroundColor: alpha(entry.main, 0.12),
            color: entry.dark,
            '&.MuiChip-clickable:hover': { backgroundColor: alpha(entry.main, 0.2) },
            '& .MuiChip-icon, & .MuiChip-deleteIcon': { color: entry.dark },
          };
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: { borderRadius: T.radius, letterSpacing: '.4px' },
        outlined: ({ ownerState }) =>
          ownerState.color === 'primary' || ownerState.color === undefined
            ? { borderColor: T.blueOutline, '&:hover': { borderColor: T.blue, backgroundColor: T.blueHover } }
            : {},
      },
    },
    MuiAlert: {
      // Baner makiety: ramka i pasek 4 px z lewej w kolorze wagi komunikatu.
      styleOverrides: {
        standard: ({ ownerState, theme }) => {
          const severity = ownerState.severity ?? 'success';
          const main = (theme as Theme).palette[severity]?.main ?? T.info;
          return {
            border: `1px solid ${main}66`,
            borderLeft: `4px solid ${main}`,
            borderRadius: T.radius,
          };
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3 },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: { fontWeight: 500, letterSpacing: '.4px', '&.Mui-selected': { color: T.blueDark } },
      },
    },
    MuiTooltip: {
      // Dymek ze szczegółami jak `#tip` makiety; szerokość tylko maksymalna.
      styleOverrides: {
        tooltip: {
          backgroundColor: T.tooltipBg,
          fontSize: 12,
          lineHeight: 1.5,
          borderRadius: 6,
          padding: '8px 10px',
          maxWidth: 380,
          boxShadow: '0 4px 14px rgba(0,0,0,.3)',
        },
        arrow: { color: T.tooltipBg },
      },
    },
    MuiFilledInput: {
      // Pole „filled” makiety: tło `--soft`, dolna linia.
      styleOverrides: {
        root: {
          backgroundColor: T.soft,
          '&:hover': { backgroundColor: '#eef1f5' },
          '&.Mui-focused': { backgroundColor: T.soft },
        },
      },
    },
    // Okno akcji jak `.dlg` makiety (partia 2): tytuł 20 px / 500, który
    // zawija się zamiast ucinać, zaokrąglenie 4 px, przyciski z prawej —
    // przy wąskim oknie przechodzą do kolejnego wiersza.
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: T.radius } },
    },
    MuiDialogTitle: {
      styleOverrides: { root: { fontSize: 20, fontWeight: 500, lineHeight: 1.4, overflowWrap: 'break-word' } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '8px 24px 16px', flexWrap: 'wrap', rowGap: 8 } },
    },
    // Przełącznik widoku (kolejki, rozliczenie, centrum ról) jak zakładki
    // działów makiety: wybrany — jasnoniebieskie tło i ciemnoniebieski napis.
    MuiToggleButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          letterSpacing: '.4px',
          borderColor: T.line,
          color: T.text2,
          '&.Mui-selected, &.Mui-selected:hover': { backgroundColor: T.blueSelected, color: T.blueDark },
        },
      },
    },
    // Wybrana pozycja listy (spis treści instrukcji, lista ról) jak
    // zaznaczona pozycja menu makiety (`nav a.on`).
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: T.radius,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: T.blueSelected,
            color: T.blueDark,
            '& .MuiListItemText-primary': { fontWeight: 500 },
          },
        },
      },
    },
  },
};

/** Opcje motywu z makiety na bazie motywu aplikacji (`appTheme`). */
export function mockupThemeFrom(base: RaThemeOptions): RaThemeOptions {
  return deepmerge(base, mockupThemeOptions) as RaThemeOptions;
}

/**
 * Gotowy motyw MUI — tak jak react-admin liczy swój (`createTheme` na
 * opcjach). Liczony raz na bazę.
 */
const cache = new WeakMap<object, Theme>();
export function createMockupTheme(base: RaThemeOptions): Theme {
  let theme = cache.get(base);
  if (!theme) {
    theme = createTheme(mockupThemeFrom(base) as ThemeOptions);
    cache.set(base, theme);
  }
  return theme;
}

/**
 * Zakres stylów globalnych: atrybut stoi na korzeniu układu i na `<body>`
 * (okna i dymki react-admina renderują się poza układem). Atrybut podnosi
 * też pierwszeństwo reguły ponad style komponentów MUI.
 */
const ON = `[${MOCKUP_LOOK_ATTR}="on"]`;

/** Selektor zaczepu w zakresie wyglądu z makiety. */
export const mockupSelector = (cls: string) => `${ON} .${cls}`;

/** Tytuł sekcji jak `.card h2` makiety: 17 px, znacznik 4 × 20 px z lewej. */
const sectionTitle = {
  position: 'relative',
  paddingLeft: 14,
  fontSize: T.sectionTitleSize,
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '.15px',
  textTransform: 'none',
  color: T.text,
  overflowWrap: 'break-word',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 2,
    width: T.markerWidth,
    height: T.markerHeight,
    borderRadius: 2,
    backgroundColor: T.blue,
  },
} as const;

/** Tytuł w karcie oddzielony linią od treści. */
const sectionTitleInCard = { paddingBottom: 10, marginBottom: 12, borderBottom: `1px solid ${T.line}` } as const;

/** Najmniejsza szerokość zawijanego napisu w komórce tabeli (wyjątek od „bez szerokości”). */
export const TABLE_WRAP_MIN = { minWidth: '6em' } as const;
export const TABLE_WRAP_MIN_SELECTOR = `${ON} td .MuiTypography-noWrap, ${ON} td .${MOCKUP_CLASS.inlinePair}`;

/** Pasek wagi z lewej w wierszu kolejki (`.sev` makiety) — kolor z palety motywu. */
const SEVERITY_COLOR: Record<MockupSeverity, string> = {
  error: T.red,
  warning: T.orange,
  success: T.green,
  info: T.blue,
};
const severityStyles = Object.fromEntries(
  (Object.keys(SEVERITY_COLOR) as MockupSeverity[]).map((sev) => [
    `${ON} tr[${MOCKUP_SEVERITY_ATTR}="${sev}"] > td:first-of-type`,
    { boxShadow: `inset 3px 0 0 ${SEVERITY_COLOR[sev]}` },
  ]),
);

/**
 * Style globalne wyglądu z makiety — elementy react-admina i wspólne
 * zaczepy (`MOCKUP_CLASS`). Wstrzykiwane tylko przy włączonym przełączniku.
 */
export const mockupGlobalStyles: Record<string, CSSObject> = {
  // Treść ekranu na białym tle z linią od menu (`main` makiety); strona szara.
  [`${ON} .RaLayout-appFrame`]: { backgroundColor: T.pageBg },
  [`${ON} .RaLayout-appFrame .RaLayout-content`]: {
    backgroundColor: T.panel,
    borderLeft: `1px solid ${T.line}`,
  },
  // Menu: zaznaczona pozycja na jasnoniebieskim tle, niebieska i pogrubiona.
  [`${ON} .RaSidebar-fixed .MuiMenuItem-root`]: { borderRadius: T.radius },
  [`${ON} .RaSidebar-fixed .RaMenuItemLink-active`]: {
    backgroundColor: T.blueSelected,
    color: T.blueDark,
    fontWeight: 500,
    '& .MuiListItemIcon-root, & .MuiSvgIcon-root': { color: T.blueDark },
    '& .MuiTypography-root': { fontWeight: 500 },
  },
  // Tytuł sekcji karty: 17 px, znacznik 4 × 20 px z lewej (`.card h2` makiety).
  [`${ON} .${MOCKUP_CLASS.sectionTitle}`]: sectionTitle,
  // W karcie sekcji tytuł oddzielony linią od treści.
  [`${ON} .MuiCardContent-root > .${MOCKUP_CLASS.sectionTitle}`]: sectionTitleInCard,
  // Partia 2: karta, która zaczyna się nagłówkiem `h6` (rozliczenie, raporty
  // dla klientów i inne ekrany back office), dostaje ten sam tytuł sekcji —
  // bez zmiany ekranów.
  [`${ON} .MuiCardContent-root > .MuiTypography-h6:first-child`]: { ...sectionTitle, ...sectionTitleInCard },
  // Pasek akcji formularza react-admina (prezlecenie, zapytanie) na białym
  // tle z linią, jak `.acts` makiety — zamiast szarego paska.
  [`${ON} .RaToolbar-desktopToolbar`]: { backgroundColor: T.panel, borderTop: `1px solid ${T.line}` },
  // Podpis pola na karcie (kierowca, przewoźnik) — 12 px, szary, jak `.f label`.
  [`${ON} .RaLabeled-label`]: { fontSize: 12, color: T.text2 },
  ...severityStyles,
  // Kafelek pulpitu jak komórka paska podsumowania makiety (`.summary.kpi`).
  [`${ON} .${MOCKUP_CLASS.tile}`]: { backgroundColor: T.soft },
  [`${ON} .${MOCKUP_CLASS.tile}:has(a):hover`]: { backgroundColor: T.blueHover },
  [`${ON} .${MOCKUP_CLASS.tileLabel}`]: {
    fontSize: T.capsSize,
    letterSpacing: T.capsSpacing,
    textTransform: 'uppercase',
    color: T.text2,
  },
  [`${ON} .${MOCKUP_CLASS.tileValue}`]: { fontSize: T.kpiValueSize, fontWeight: 500, lineHeight: 1.4 },
  // Napis jednowierszowy z wielokropkiem (`Typography noWrap` — kontrahent i relacja w banku, typ
  // auta, autor wariantu, podpis kroku zlecenia) zawija się w kolejny wiersz (pytanie Bartka
  // 29.10.2026 o wygląd na różnych rozdzielczościach: przy 1280 px banku ucinało nazwy
  // kontrahentów i miast). Słowo łamie się dopiero, gdy nie mieści się w całości.
  [`${ON} .MuiTypography-noWrap`]: { whiteSpace: 'normal', overflow: 'visible', overflowWrap: 'break-word' },
  // Opis skracany do 2–3 wierszy z pełną treścią w dymku (`title` — karta roli, „Co nowego”,
  // opis przełącznika) pokazuje całą treść. Skrót z przyciskiem „więcej” (panel przewodnika)
  // nie ma `title` i zostaje.
  [`${ON} .MuiTypography-root[title]`]: { WebkitLineClamp: 'unset' },
  // W komórce tabeli zawinięty napis ma co najmniej 6em (ok. 84 px): tabela o automatycznym
  // układzie ściskała kolumnę z zawijanym tekstem do najdłuższego słowa — przy 1024 px relacja
  // „PL Katowice → CZ Brno” zajmowała 6 wierszy w kolumnie 58 px. Minimum, nie sztywna
  // szerokość — dłuższy tekst dalej się zawija (wyjątek w teście `mockupTheme.test.ts`).
  [TABLE_WRAP_MIN_SELECTOR]: TABLE_WRAP_MIN,
  // Tekst z dopiskiem w jednym wierszu listy (użytkownik i e-mail, numer i nazwa): gdy obu
  // nie starcza miejsca, dopisek schodzi do kolejnego wiersza — zamiast ściskać główny tekst
  // do kilkudziesięciu pikseli i łamać go w środku słowa.
  [`${ON} .${MOCKUP_CLASS.inlinePair}`]: { flexWrap: 'wrap', rowGap: 0 },
  // Nagłówek kolumny listy react-admina zawija się (gęste listy ustawiają mu `nowrap`), więc
  // kolumna nie jest szersza od swojej treści tylko z powodu nagłówka.
  [`${ON} .RaDatagrid-root .RaDatagrid-headerCell`]: { whiteSpace: 'normal' },
};
